import { Alert, AlertDescription, AlertIcon, Text, VStack } from '@chakra-ui/react'
import { useProfile } from '@contexts/user/ProfileContext'
import {
  BloodPressure,
  Encounter,
  EncounterType,
  PrenatalExamData,
  VitalSource,
  VitalSourceKey,
} from '@hb/shared'
import { useMemo } from 'react'
import { useField } from 'react-final-form'

const MS_PER_DAY = 86_400_000

// §2.4: Inline clinical alerts for out-of-range vital signs on antepartum visits.
// Reads vitals + FHT from form state and surfaces red/orange banners when thresholds are exceeded.
// FHT is read from data.fetalHeartTones (prenatal exam field, same form step).
export const PrenatalVitalsAlerts = () => {
  const { encounters, assessmentId } = useProfile()

  const {
    input: { value: bp },
  } = useField<BloodPressure | undefined>('data.vital.bloodPressure', {
    subscription: { value: true },
  })
  const {
    input: { value: pulse },
  } = useField<number | undefined>('data.vital.pulse', { subscription: { value: true } })
  const {
    input: { value: temp },
  } = useField<number | undefined>('data.vital.temperature', { subscription: { value: true } })
  const {
    input: { value: fht },
  } = useField<number | undefined>('data.fetalHeartTones', { subscription: { value: true } })
  const {
    input: { value: currentWeight },
  } = useField<number | undefined>('data.vital.weight', { subscription: { value: true } })
  const {
    input: { value: gestationalAgeDays },
  } = useField<number | undefined>('data.gestationalAge', { subscription: { value: true } })
  const {
    input: { value: encounterDate },
  } = useField<string | undefined>('date', { subscription: { value: true } })

  const {
    input: { value: vitalSources },
  } = useField<Partial<Record<VitalSourceKey, VitalSource>> | undefined>('data.vitalSources', {
    subscription: { value: true },
  })

  const priorAntepartumEncounters = useMemo(
    () =>
      ((encounters.dataArr ?? []) as Encounter[]).filter(
        enc =>
          enc.assessmentId === assessmentId &&
          enc.type === EncounterType.ANTEPARTUM &&
          !!enc.authentication &&
          !!enc.date,
      ),
    [encounters.dataArr, assessmentId],
  )

  // §2.4: Weight gain > 5 lbs/week after 20w (140 days GA).
  const weightGainAlert = useMemo(() => {
    if (typeof currentWeight !== 'number' || currentWeight <= 0) return false
    if (typeof gestationalAgeDays !== 'number' || gestationalAgeDays < 140) return false
    const prev = priorAntepartumEncounters
      .filter(
        enc =>
          (enc.data as PrenatalExamData & { vital?: { weight?: number } })?.vital?.weight != null,
      )
      .sort((a, b) => new Date(b.date ?? 0).getTime() - new Date(a.date ?? 0).getTime())[0]
    if (!prev?.date) return false
    const prevWeight = (prev.data as any)?.vital?.weight as number | undefined
    if (prevWeight == null) return false
    const curDateMs = encounterDate ? new Date(encounterDate).getTime() : Date.now()
    const prevDateMs = new Date(prev.date).getTime()
    const daysBetween = (curDateMs - prevDateMs) / MS_PER_DAY
    if (daysBetween <= 0) return false
    const gainPerWeek = ((currentWeight - prevWeight) / daysBetween) * 7
    return gainPerWeek > 5
  }, [currentWeight, gestationalAgeDays, priorAntepartumEncounters, encounterDate])

  // §2.4: Rotating Tier 3 prompt — surfaces if Nutrition/Hydration or Physical Activity
  // has not been documented in >28 days across prior authenticated encounters.
  const showNutritionPrompt = useMemo(() => {
    const lastDocumented = priorAntepartumEncounters
      .filter(enc => {
        const d = enc.data as PrenatalExamData
        return d?.nutritionHydration || d?.physicalActivity
      })
      .sort((a, b) => new Date(b.date ?? 0).getTime() - new Date(a.date ?? 0).getTime())[0]
    if (!lastDocumented?.date) return true // never documented
    const daysSince = (Date.now() - new Date(lastDocumented.date).getTime()) / MS_PER_DAY
    return daysSince > 28
  }, [priorAntepartumEncounters])

  const bpElevated = bp && (bp.systolic >= 140 || bp.diastolic >= 90)
  const pulseAbnormal = typeof pulse === 'number' && pulse > 0 && (pulse < 60 || pulse > 100)
  const feverPresent = typeof temp === 'number' && temp >= 100.4
  const fhtAbnormal = typeof fht === 'number' && fht > 0 && (fht < 110 || fht > 160)

  // §H: Patient-reported source flags
  const bpPatientReported = vitalSources?.bloodPressure === 'pt-reported'
  const tempPatientReported = vitalSources?.temperature === 'pt-reported'
  const pulsePatientReported = vitalSources?.pulse === 'pt-reported'

  if (
    !bpElevated &&
    !pulseAbnormal &&
    !feverPresent &&
    !fhtAbnormal &&
    !weightGainAlert &&
    !showNutritionPrompt
  )
    return null

  return (
    <VStack align="stretch" spacing={2} mt={2}>
      {bpElevated ? (
        <Alert status="error" borderRadius="md" fontSize="sm">
          <AlertIcon />
          <AlertDescription>
            {bpPatientReported
              ? `BP ${bp.systolic}/${bp.diastolic} — patient-reported. Confirm at next in-person visit.`
              : 'Blood pressure is elevated (≥140/90). Evaluate for hypertensive disorders of pregnancy. Consider repeating in 15 minutes.'}
          </AlertDescription>
        </Alert>
      ) : null}
      {fhtAbnormal ? (
        <Alert status="error" borderRadius="md" fontSize="sm">
          <AlertIcon />
          <AlertDescription>
            Fetal heart rate is outside the normal range (110–160 bpm). Evaluate fetal status.
          </AlertDescription>
        </Alert>
      ) : null}
      {weightGainAlert ? (
        <Alert status="warning" borderRadius="md" fontSize="sm">
          <AlertIcon />
          <AlertDescription>
            Weight gain exceeds 5 lbs/week since the last visit. Document clinically and evaluate
            for fluid retention or rapid fetal growth.
          </AlertDescription>
        </Alert>
      ) : null}
      {feverPresent ? (
        <Alert status="warning" borderRadius="md" fontSize="sm">
          <AlertIcon />
          <AlertDescription>
            {tempPatientReported
              ? `Temperature ${temp}°F — patient-reported. Confirm at next in-person visit.`
              : 'Temperature indicates possible fever (≥100.4°F). Evaluate for infection.'}
          </AlertDescription>
        </Alert>
      ) : null}
      {pulseAbnormal ? (
        <Alert status="warning" borderRadius="md" fontSize="sm">
          <AlertIcon />
          <AlertDescription>
            {pulsePatientReported
              ? `Pulse ${pulse} bpm — patient-reported. Confirm at next in-person visit.`
              : 'Pulse is outside the normal range (60–100 bpm). Evaluate clinical context.'}
          </AlertDescription>
        </Alert>
      ) : null}
      {showNutritionPrompt ? (
        <Alert status="info" borderRadius="md" fontSize="sm">
          <AlertIcon />
          <AlertDescription lineHeight={1.3}>
            <Text as="span" fontWeight={500}>
              Rotating prompt:{' '}
            </Text>
            Nutrition, hydration, or physical activity has not been documented in the past 4 weeks.
            Consider documenting in the Interval History section.
          </AlertDescription>
        </Alert>
      ) : null}
    </VStack>
  )
}
